import type { Rect } from "./geometry";
import type { SceneDocument, ScenePage } from "./schema";

/**
 * Page sizes and the board (docs/04-dokumentformat.md): `format` is the default
 * size; designs without print format may give single pages their own size, and
 * the editor can show all pages side by side on one canvas ("board").
 */

/** Gap between pages that flow automatically on the board. */
export const BOARD_GAP = 160;
/** Smallest and largest page edge the editor offers. */
export const PAGE_MIN = 16;
export const PAGE_MAX = 20000;

/** Own page sizes and the board only exist for plain designs (not print, not video). */
export function supportsBoard(doc: Pick<SceneDocument, "kind" | "format">): boolean {
  return doc.kind === "design" && !doc.format.print;
}

export interface PageSize {
  width: number;
  height: number;
}

/** Size of a page in pixels. */
export function pageSize(doc: Pick<SceneDocument, "kind" | "format">, page?: ScenePage): PageSize {
  if (page && supportsBoard(doc)) {
    return { width: page.width ?? doc.format.width, height: page.height ?? doc.format.height };
  }
  return { width: doc.format.width, height: doc.format.height };
}

/**
 * The document as seen by one page: `format` carries the size of that page. For
 * everything that measures against `doc.format` (snapping, grids, preflight,
 * export) – identical to `doc` when the page has the default size.
 */
export function pageDocument(doc: SceneDocument, pageIndex: number): SceneDocument {
  const page = doc.pages[pageIndex];
  if (!page) return doc;
  const size = pageSize(doc, page);
  if (size.width === doc.format.width && size.height === doc.format.height) return doc;
  return { ...doc, format: { ...doc.format, ...size } };
}

/** True when pages differ in size. */
export function hasMixedSizes(doc: SceneDocument): boolean {
  return doc.pages.some((page) => {
    const size = pageSize(doc, page);
    return size.width !== doc.format.width || size.height !== doc.format.height;
  });
}

/** Largest edge over all pages (export limits). */
export function maxPageEdge(doc: SceneDocument): number {
  let max = 0;
  for (const page of doc.pages) {
    const size = pageSize(doc, page);
    max = Math.max(max, size.width, size.height);
  }
  return max;
}

/**
 * Position and size of every page on the board. Pages without a position follow
 * the previous page in a row (same top edge as the first page).
 */
export function boardRects(doc: SceneDocument): Rect[] {
  const rects: Rect[] = [];
  let cursor = 0;
  let top = 0;
  doc.pages.forEach((page, index) => {
    const size = pageSize(doc, page);
    const x = page.x ?? cursor;
    const y = page.y ?? (index === 0 ? 0 : top);
    if (index === 0) top = y;
    rects.push({ x, y, width: size.width, height: size.height });
    cursor = Math.max(cursor, x + size.width + BOARD_GAP);
    if (page.x !== undefined && page.y !== undefined) cursor = x + size.width + BOARD_GAP;
  });
  return rects;
}

/** Bounding rectangle of all pages on the board. */
export function boardBounds(doc: SceneDocument): Rect {
  const rects = boardRects(doc);
  let left = Infinity;
  let topEdge = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const rect of rects) {
    left = Math.min(left, rect.x);
    topEdge = Math.min(topEdge, rect.y);
    right = Math.max(right, rect.x + rect.width);
    bottom = Math.max(bottom, rect.y + rect.height);
  }
  if (!Number.isFinite(left))
    return { x: 0, y: 0, width: doc.format.width, height: doc.format.height };
  return { x: left, y: topEdge, width: right - left, height: bottom - topEdge };
}

/** Index of the page whose rectangle contains the point (topmost = last), or -1. */
export function pageAtBoardPoint(rects: readonly Rect[], point: { x: number; y: number }): number {
  for (let index = rects.length - 1; index >= 0; index--) {
    const rect = rects[index]!;
    if (
      point.x >= rect.x &&
      point.x <= rect.x + rect.width &&
      point.y >= rect.y &&
      point.y <= rect.y + rect.height
    ) {
      return index;
    }
  }
  return -1;
}

/** Writes the effective positions into the pages (a stable starting point for dragging). */
export function materializeBoard(doc: SceneDocument): void {
  const rects = boardRects(doc);
  doc.pages.forEach((page, index) => {
    const rect = rects[index]!;
    page.x = Math.round(rect.x);
    page.y = Math.round(rect.y);
  });
}

/** Lines all pages up in one row (or wrapped into rows of `columns`), top aligned per row. */
export function arrangeBoard(doc: SceneDocument, columns = doc.pages.length): void {
  const perRow = Math.max(1, columns);
  let y = 0;
  let x = 0;
  let rowHeight = 0;
  doc.pages.forEach((page, index) => {
    const size = pageSize(doc, page);
    if (index > 0 && index % perRow === 0) {
      y += rowHeight + BOARD_GAP;
      x = 0;
      rowHeight = 0;
    }
    page.x = x;
    page.y = y;
    x += size.width + BOARD_GAP;
    rowHeight = Math.max(rowHeight, size.height);
  });
}

/**
 * Free spot for a new page of the given size: right of the page `afterIndex`
 * (same top edge), moving further right while it would overlap another page.
 */
export function freeBoardSpot(
  doc: SceneDocument,
  afterIndex: number,
  size: PageSize,
): { x: number; y: number } {
  const rects = boardRects(doc);
  const base = rects[afterIndex] ?? rects[rects.length - 1] ?? { x: 0, y: 0, width: 0, height: 0 };
  let x = base.x + base.width + BOARD_GAP;
  const y = base.y;
  for (let attempt = 0; attempt < 200; attempt++) {
    const clash = rects.find(
      (rect) =>
        x < rect.x + rect.width + BOARD_GAP / 2 &&
        x + size.width > rect.x - BOARD_GAP / 2 &&
        y < rect.y + rect.height &&
        y + size.height > rect.y,
    );
    if (!clash) break;
    x = clash.x + clash.width + BOARD_GAP;
  }
  return { x: Math.round(x), y: Math.round(y) };
}
